import { DiscountTypeSchema } from '@ca/common'
import { DealTypeSchema } from '@ca/common/types'
import { z } from 'zod'

const corridorOptionsSchema = z.object({
  options: z.array(
    z.object({
      // value to pass to erp and build the label off
      value: z.string().or(z.number()),
      // will use custom label if provided
      label: z.string().optional(),
    })
  ),
})

const introTagsSchema = z.object({
  tags: z.array(z.string()),
  initial: z.string(),
})

const storesSchema = z.object({
  store_refs: z.array(z.string()).nullable(),
})

const bannerSchema = z.object({
  deal: z.string(),
  require: z.array(z.string()),
  key: z.string(),
})

const discountsSchema = z.object({
  modes: z.array(DiscountTypeSchema),
})

export type IntroTagsSchema = z.infer<typeof introTagsSchema>

const countriesSchema = z.object({
  codes: z.array(z.string()),
})

const discountsValidationSchema = z.object({
  startsWith: z.array(z.string()),
})

export type DiscountsValidationSchema = z.infer<
  typeof discountsValidationSchema
>

const introTypeSchema = z.object({
  type: z.enum(['tiles:sv', 'tiles:vf', 'deals']),
  tag: z.string().optional(),
})

const framePriceSchema = z.object({
  price: z.number().optional(),
  key: z.string().optional(),
})

const frameLimitsSchema = z.object({
  minPrice: z.number().optional(),
  maxPrice: z.number().optional(),
})

const frameFiltersSchema = z.object({
  fcatalog: z.array(z.string()),
})

const brandOptionSchema = z.object({
  name: DealTypeSchema,
  requiredTags: z.array(z.string()).optional(),
  excludedTags: z.array(z.string()).optional(),
  // We always normalize to nested array to create an array of buckets
  priorityTags: z
    .union([
      z.array(z.string()).transform((tags) => [tags]),
      z.array(z.array(z.string())),
    ])
    .optional(),
})

const brandsSchema = z.object({
  options: z.array(brandOptionSchema),
})

const paymentAmountStepSchema = z.object({
  // `percent` of the payable total, `snapPercent` of track width.
  percent: z.number().positive().max(100).optional(),
  snapPercent: z.number().min(0).max(50).optional(),
})

const orderHistorySchema = z.object({
  // Shows payment details and badges
  payments: z.boolean(),
  // Allows paying outstanding balances from order history; off unless set
  pay: z.boolean().catch(false),
})

const measurementMethodSchema = z.object({
  pd: z.enum(['sdk', 'optical', 'disabled']),
  sh: z.enum(['sdk', 'optical', 'disabled']),
  // Which repeat press on the SH review sheet offers manual point adjustment
  // instead of another capture: 1 offers it on the first repeat, 2 after one
  // retry, and so on. 0 keeps it switched off.
  // `catch` rather than `default` so a malformed value cannot invalidate the
  // whole condition and silently drop pd/sh back to the SDK.
  shAdjustmentRetries: z.number().int().min(0).catch(0),
})

export type BrandsSchema = z.infer<typeof brandsSchema>
export type BrandOptionSchema = z.infer<typeof brandOptionSchema>

export const ConfigConditionsSchemas = {
  corridor: corridorOptionsSchema,
  tags: introTagsSchema,
  stores: storesSchema,
  banner: bannerSchema,
  discounts: discountsSchema,
  countries: countriesSchema,
  discountsValidation: discountsValidationSchema,
  introType: introTypeSchema,
  framePrice: framePriceSchema,
  frameLimits: frameLimitsSchema,
  frameFilters: frameFiltersSchema,
  brands: brandsSchema,
  orderHistory: orderHistorySchema,
  measurementMethod: measurementMethodSchema,
  paymentAmountStep: paymentAmountStepSchema,
} as const

export type ConfigConditionSchema = keyof typeof ConfigConditionsSchemas

export type SelectSchema<T extends ConfigConditionSchema> = z.infer<
  (typeof ConfigConditionsSchemas)[T]
>

export const ORDER_HISTORY = 'orderHistory:enabled'
export const PAYMENT_AMOUNT_STEP = 'payments:amountStep'
